import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Token storage helpers ─────────────────────────────────────────────────────
// JWT keys (new):    'access_token'  and 'refresh_token'
// Legacy DRF key:    'token'   (preserved during transition period)

export const getAccessToken  = () => localStorage.getItem('access_token');
export const getRefreshToken = () => localStorage.getItem('refresh_token');
export const getLegacyToken  = () => localStorage.getItem('token');

export const storeJwtTokens = ({ access, refresh }) => {
  if (access)  localStorage.setItem('access_token',  access);
  if (refresh) localStorage.setItem('refresh_token', refresh);
};

export const clearAllTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token');  // Clear legacy token too on logout
};

// ── Silent refresh helper ─────────────────────────────────────────────────────
// Prevents concurrent refresh races: multiple 401s share the same refresh promise.
let _refreshPromise = null;

const silentRefresh = async () => {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token available');

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || '/api'}/auth/jwt/refresh/`,
      { refresh: refreshToken },
    );

    storeJwtTokens(response.data);
    return response.data.access;
  })();

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
};

// ── Request interceptor ───────────────────────────────────────────────────────
// Priority: JWT Bearer → legacy DRF Token → no auth header
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    const legacyToken = getLegacyToken();

    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    } else if (legacyToken) {
      // Transition period: existing sessions still authenticated via DRF token
      config.headers['Authorization'] = `Token ${legacyToken}`;
    }

    // Active language for localised responses
    const language = localStorage.getItem('language') || 'en';
    config.headers['Accept-Language'] = language;



    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status         = error.response?.status;
    const originalConfig = error.config;

    // On 401 with a JWT access token: attempt silent refresh then retry
    if (status === 401 && !originalConfig._retried && getAccessToken()) {
      originalConfig._retried = true; // prevent infinite retry loops

      try {
        const newAccessToken = await silentRefresh();
        originalConfig.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalConfig); // retry original request with fresh token
      } catch {
        // Refresh also failed — clear everything and redirect to login
        clearAllTokens();
        const path = window.location.pathname;
        const isPublicPath = ['/login', '/register', '/'].includes(path) || 
          ['/portal/', '/s/', '/sign/', '/schedule/', '/test-widget/', '/proposals/', '/lead-form'].some(prefix => path.startsWith(prefix));
        if (!isPublicPath) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }

    // On 401 with legacy token (no JWT) — clear and redirect
    if (status === 401 && !getAccessToken()) {
      clearAllTokens();
      const path = window.location.pathname;
      const isPublicPath = ['/login', '/register', '/'].includes(path) || 
        ['/portal/', '/s/', '/sign/', '/schedule/', '/test-widget/', '/proposals/', '/lead-form'].some(prefix => path.startsWith(prefix));
      if (!isPublicPath) {
        window.location.href = '/login';
      }
    }

    // Only log in development — never expose internals in production
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('API Error:', {
        status,
        url:  error.config?.url,
        data: error.response?.data,
      });
    }

    return Promise.reject(error);
  },
);

// ── Legacy compatibility helper ───────────────────────────────────────────────
// Used by existing login flow that stores 'token' key.
// Remains functional during the transition period.
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Token ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// ── Request config builder ────────────────────────────────────────────────────
export const createRequestConfig = (customHeaders = {}, customBody = {}) => ({
  headers: {
    ...api.defaults.headers,
    ...customHeaders,
  },
  data: customBody,
});

export default api;
// ── Server-side logout helper ─────────────────────────────────────────────────
// Calls /api/auth/logout/ to invalidate the server-side Token + JWT refresh,
// then clears all local auth state regardless of server response.
export const logout = async () => {
  try {
    const refreshToken = getRefreshToken();
    await api.post('/auth/logout/', refreshToken ? { refresh: refreshToken } : {});
  } catch {
    // Server-side invalidation failed (e.g. token already expired) — still clear locally
  } finally {
    clearAllTokens();
  }
};
